import { useSelector } from 'react-redux';
import { useCurrentEnterprise, useEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { ActionType, RouteName, TextKey } from '@mews-commander/enums';
import { WithGuideLink } from '@mews-commander/types';
import { compactArray } from '@mews/fp';
import { LocalizedText, lt, useDialog } from '@mews/framework';
import { useResolveRoute } from '@mews/routing';
import { IconName } from '@optimus-web/icons';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { hasCurrentEnterpriseMultiPropertyAccess } from '@mews-commander/legacy-entities/src/selectors/session/enterpriseProductSubscriptions';
import { useEnterpriseBuildLocation } from '@mews-commander/ui';
import { Breadcrumbs } from '../../../../../components/BreadcrumbList';
import { FilterSidebarLayoutBase, FilterSidebarLayoutContent } from '../../../../../components/layout';
import { HeadingAction } from '../../../../../legacy/React/Components/HeadingAction/Action/types';
import { useTabsConfig } from '../../../../modalManager/tabs';
import { TabData } from '../../../../modalManager/types';
import { useUserDetailContext } from '../../../context/userDetailContext';
import { useAuthorizationCheck } from '../../../hooks/useAuthorizationCheck';
import { AddUserDialog } from '../../AddLocalUser';
import { defaultFilterValues, filterSchema, FilterValues } from '../filterSchema';
import { FilterFields } from '../FilterFields';
import { UsersTable } from './UsersTable';

export const Users: React.FC<React.PropsWithChildren<WithGuideLink & { activeTab: Mews.TextKey }>> = ({
    guideLink,
    activeTab,
}) => {
    const { closeUserDetail } = useUserDetailContext();

    const [dialogProps, toggleDialog] = useDialog();

    const openAddUserDialog = useAuthorizationCheck(toggleDialog);

    const actions: HeadingAction[] = [
        {
            onClick: openAddUserDialog,
            icon: IconName.Add,
            text: <LocalizedText textKey={TextKey.UserManagementAddNewUserToProperty} />,
            'data-test-icon-button': IconName.Add,
            type: ActionType.Link,
        },
    ];

    const isInsidePortfolio = useCurrentEnterprise()?.isPartOfPortfolio;

    const propertyListTabs = usePropertyListTabs();
    const portfolioTabsConfig = useTabsConfig({
        tabs: propertyListTabs,
        activeTab,
        onTabClick: closeUserDetail,
    });

    const enterpriseId = useCurrentEnterpriseId();
    const { name } = useEnterprise(enterpriseId);

    const buildLocation = useEnterpriseBuildLocation();
    const dashboardUrl = buildLocation({ to: 'Dashboard/Index' }).href;

    const breadcrumbs: Breadcrumbs = [
        { text: name, url: dashboardUrl },
        { text: <LocalizedText textKey={TextKey.UserPlural} />, active: true },
    ];

    return (
        <>
            <FilterSidebarLayoutBase
                filterSidebarOpenedByDefault={false}
                tabsConfig={isInsidePortfolio ? portfolioTabsConfig : undefined}
                guideLink={guideLink}
                actions={actions}
                breadcrumbs={breadcrumbs}
                withToggleFilterHeaderAction={false}
            >
                {(sidebarToggleProps) => (
                    <FilterSidebarLayoutContent<FilterValues>
                        {...sidebarToggleProps}
                        filterSchema={filterSchema}
                        filterSidebar={{ title: lt(TextKey.Filter), fields: <FilterFields /> }}
                        defaultFilterValues={defaultFilterValues}
                        submitButtonLabel={lt(TextKey.Filter)}
                        withClearFilters
                    >
                        {({ filterValues }) => <UsersTable filterValues={filterValues} {...sidebarToggleProps} />}
                    </FilterSidebarLayoutContent>
                )}
            </FilterSidebarLayoutBase>
            <AddUserDialog {...dialogProps} />
        </>
    );
};

function usePropertyListTabs(): TabData[] {
    const resolveRoute = useResolveRoute();

    const hasMultiProperty = useSelector(hasCurrentEnterpriseMultiPropertyAccess);

    return compactArray([
        {
            key: TextKey.PropertyUsers,
            url: resolveRoute(RouteName.UserManagementPropertyUsersIndex),
            'data-test-tab': 'property-users',
        },
        hasMultiProperty
            ? {
                  key: TextKey.PortfolioUserPlural,
                  url: resolveRoute(RouteName.UserManagementCentralUsersIndex),
                  'data-test-tab': 'central-users',
              }
            : undefined,
    ]);
}
